import apiClient from '../client';

// The fleet roster — what trucks exist, not what paperwork they have.
//
// Not to be confused with the vault's vehicle roster
// (`document.service.getRoster('vehicle')`), which answers a different
// question: that one returns compliance state per truck and knows nothing
// about a VIN. This is the asset record itself.
//
// NOTE: the shared apiClient's response interceptor returns response.data,
// so every call here resolves to the response BODY.

export type VehicleStatus = 'active' | 'maintenance' | 'retired';
export type OwnershipKind = 'cash' | 'financed' | 'leased';

// How the truck is paid for. The API only sends this — and the property
// tax fields — to AO and CA; for anyone else they are simply absent.
export interface VehicleOwnership {
  kind?: OwnershipKind;
  lienholder?: string;
  startedOn?: string | null;
  maturesOn?: string | null;
  termMonths?: number;
  invoiceValue?: number;
  amountFinanced?: number;
  taxFinanced?: number;
  downPayment?: number;
  monthlyPayment?: number;
  totalCost?: number;
  interestRate?: number;
  notes?: string;
}

// Detail fields shared by the record and the input. Dates travel as ISO
// strings; on input a blank string or null clears the field.
interface VehicleDetail {
  bodyType?: string;
  fuel?: string;
  titleNumber?: string;
  registrationExpiresOn?: string | null;
  registrationReceived?: boolean;
  insuranceRenewsOn?: string | null;
  onScheduleB?: boolean;
  propertyTaxAccount?: string;
  propertyTaxAmount?: number | null;
}

export interface Vehicle extends VehicleDetail {
  _id: string;
  companyId: string;
  unitNumber: string;
  vin?: string;
  year?: number;
  make?: string;
  model?: string;
  licensePlate?: string;
  plateState?: string;
  serviceAreaId: number | null;
  status: VehicleStatus;
  retiredAt: string | null;
  notes?: string;
  ownership?: VehicleOwnership;
  createdAt?: string;
  updatedAt?: string;
}

export type VehicleInput = VehicleDetail & {
  unitNumber: string;
  vin?: string;
  year?: number | null;
  make?: string;
  model?: string;
  licensePlate?: string;
  plateState?: string;
  serviceAreaId?: number | null;
  status?: VehicleStatus;
  notes?: string;
  ownership?: { [K in keyof VehicleOwnership]?: VehicleOwnership[K] | null };
};

export interface VehicleImportRow {
  row: number;
  unit: string;
  vin: string;
  ok: boolean;
  action: 'create' | 'update' | 'skip';
  message: string;
  filled?: string[];
  warnings?: string[];
}

export interface VehicleImportSummary {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  filledFromVin: number;
  vinLookupUnavailable: boolean;
  written?: number;
}

export type DecodedVin = Partial<Pick<Vehicle, 'year' | 'make' | 'model' | 'bodyType' | 'fuel'>>;

function describe(err: any, fallback: string): string {
  // The unit number is unique per company and the server says so with a
  // 409 — surfaced verbatim, because "that unit number already exists"
  // tells someone exactly what to change.
  return err?.response?.data?.message || fallback;
}

export class VehicleService {
  private static readonly BASE = '/api/vehicles';

  static async list(params: { includeRetired?: boolean; search?: string } = {}): Promise<Vehicle[]> {
    try {
      const query = new URLSearchParams();
      if (params.includeRetired) query.append('includeRetired', 'true');
      if (params.search) query.append('search', params.search);
      const body = await apiClient.get<any, { vehicles: Vehicle[] }>(`${this.BASE}?${query.toString()}`);
      return body.vehicles || [];
    } catch (err) {
      throw new Error(describe(err, 'Could not load the fleet'));
    }
  }

  static async get(id: string): Promise<Vehicle> {
    try {
      const body = await apiClient.get<any, { vehicle: Vehicle }>(`${this.BASE}/${id}`);
      return body.vehicle;
    } catch (err) {
      throw new Error(describe(err, 'Could not load that vehicle'));
    }
  }

  static async create(input: VehicleInput): Promise<Vehicle> {
    try {
      const body = await apiClient.post<any, { vehicle: Vehicle }>(this.BASE, input);
      return body.vehicle;
    } catch (err) {
      throw new Error(describe(err, 'Could not add that vehicle'));
    }
  }

  static async update(id: string, input: Partial<VehicleInput>): Promise<Vehicle> {
    try {
      const body = await apiClient.patch<any, { vehicle: Vehicle }>(`${this.BASE}/${id}`, input);
      return body.vehicle;
    } catch (err) {
      throw new Error(describe(err, 'Could not save that vehicle'));
    }
  }

  // Rows keyed by the template's column keys. dryRun defaults to true on
  // the server too.
  static async import(rows: Record<string, string>[], dryRun: boolean)
    : Promise<{ results: VehicleImportRow[]; summary: VehicleImportSummary }> {
    return apiClient.post<any, { results: VehicleImportRow[]; summary: VehicleImportSummary }>(
      `${this.BASE}/import`, { rows, dryRun },
    );
  }

  // What the VIN says the truck is. Never throws: a failed lookup is just
  // nothing filled in.
  static async decodeVin(vin: string): Promise<DecodedVin> {
    try {
      const body = await apiClient.get<any, { vehicle: DecodedVin }>(`${this.BASE}/vin/${encodeURIComponent(vin)}`);
      return body.vehicle || {};
    } catch {
      return {};
    }
  }

  // Retiring clears the truck's compliance obligations; reactivating
  // rebuilds them. Both are AO/CA only, and the API enforces it — the UI
  // hides the buttons as a courtesy, not as the gate.
  static async retire(id: string, reason?: string): Promise<Vehicle> {
    try {
      const body = await apiClient.post<any, { vehicle: Vehicle }>(`${this.BASE}/${id}/retire`, { reason });
      return body.vehicle;
    } catch (err) {
      throw new Error(describe(err, 'Could not retire that vehicle'));
    }
  }

  static async reactivate(id: string): Promise<Vehicle> {
    try {
      const body = await apiClient.post<any, { vehicle: Vehicle }>(`${this.BASE}/${id}/reactivate`, {});
      return body.vehicle;
    } catch (err) {
      throw new Error(describe(err, 'Could not bring that vehicle back'));
    }
  }
}

export default VehicleService;
